import { useCallback, useContext } from "react";
import { mutate } from "swr";
import createPersistedState from "./createPersistedState";
import { useHydrated } from "./useHydrated";
import { StoreContext } from "./store";
import { Tenant } from "./types";
import useData from "./useData";
import { useImpersonateParam } from "./useImpersonateParam";

export const TENANTS_ENDPOINT = "/web/tenants";
/** Clear the SWR cache for tenants. */
export function invalidateTenantCache() {
  mutate(TENANTS_ENDPOINT, undefined);
}

const useTenantIdState = createPersistedState<string>("tenant_id");

export interface TenantsResponse {
  tenants: Tenant[];
}

export const tenantDisplayName = (tenant: Tenant, short = false): string => {
  if (tenant.kind == "sandbox") {
    if (tenant.sandbox_owned_by_current_user) {
      return short ? "Personal" : "Your Personal Organization";
    } else {
      const owner = tenant.sandbox_owner_name || "Someone else";
      return short ? owner : `${owner}'s Personal Organization`;
    }
  } else {
    return tenant.name || tenant.id;
  }
};

const useTenant = () => {
  const { forceTenantSwitcher, setForceTenantSwitcher } =
    useContext(StoreContext);
  const [storedTenantId, setStoredTenantId] = useTenantIdState("");
  const hydrated = useHydrated();

  const impersonateParam = useImpersonateParam();

  // Don't fetch until router is ready (impersonateParam is undefined while hydrating)
  // SWR skips fetch when the key function returns null/undefined
  const apiPath = () => {
    if (impersonateParam === undefined) {
      return null;
    }
    return impersonateParam
      ? `${TENANTS_ENDPOINT}?impersonate=${encodeURIComponent(
          impersonateParam
        )}`
      : TENANTS_ENDPOINT;
  };

  const { data, refresh } = useData<TenantsResponse>(apiPath);
  const tenants = data?.tenants || [];

  const setTenant = useCallback(
    (tenant: Tenant) => {
      setStoredTenantId(tenant.id);
      setForceTenantSwitcher(false);
    },
    [setStoredTenantId, setForceTenantSwitcher]
  );

  const switchTenant = useCallback(() => {
    setForceTenantSwitcher(true);
  }, [setForceTenantSwitcher]);

  const selectedTenantIndex = tenants.findIndex(
    (tenant) => tenant.id === storedTenantId
  );
  // The stored selection wins. Otherwise we fall back to an implicit pick
  // when there's no real ambiguity — a single tenant overall, or a single
  // production tenant alongside any number of sandboxes — leaving `tenant`
  // null only when the user genuinely needs to choose. Hydration gates the
  // whole thing so the first render doesn't race the stored selection.
  const matchedTenant = tenants[selectedTenantIndex];
  const onlyTenant = tenants.length === 1 ? tenants[0] : null;
  const productionTenants = tenants.filter((t) => t.kind === "production");
  const soloProductionTenant =
    productionTenants.length === 1 ? productionTenants[0] : null;
  const tenant = hydrated
    ? matchedTenant ?? onlyTenant ?? soloProductionTenant
    : null;

  const shouldShowSwitcher =
    forceTenantSwitcher || (hydrated && tenants.length > 0 && !tenant);

  return {
    shouldShowSwitcher,
    tenant,
    tenants,
    setTenant,
    switchTenant,
    refresh,
  };
};

export default useTenant;
